<template>
  <div
    v-if="widget"
    class="hbt"
  >
    <div class="hbt-chart-head">
      <span
        class="hbt-h3"
        style="font-size: 19px;"
      >{{ title }}</span>
      <span class="hbt-kicker-mut">{{ indicatorEn }}</span>
      <span
        class="hbt-tag"
        style="margin-left: auto;"
        :style="trendStyle"
      >{{ trendLabel }}</span>
    </div>
    <div
      class="d-flex align-items-baseline"
      style="gap: 6px; margin: 8px 0 10px;"
    >
      <span
        class="hbt-num"
        style="font-size: 30px;"
      >{{ lastText }}</span>
      <span class="hbt-mut">{{ unit }} · {{ deltaText }}</span>
    </div>
    <svg
      class="hbt-spark"
      viewBox="0 0 320 88"
      preserveAspectRatio="none"
    >
      <polyline
        :points="points"
        fill="none"
        stroke="#ec3013"
        stroke-width="2"
      />
    </svg>
    <div
      class="d-flex justify-content-between hbt-mut"
      style="font-size: 11px; margin-top: 5px;"
    >
      <span>{{ minLabel }}</span><span>{{ maxLabel }}</span>
    </div>
  </div>
</template>

<script>
import {
  HBT_REFRESH, HBT_ZONES, hbtLoadIndicators, hbtLoadMeasures,
} from '@/cubus/components/dashboard/habits/habitsCore'

export default {
  name: 'CbsWidgetHabitChart',
  props: {
    widget: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      indicator: null,
      measures: [],
    }
  },
  computed: {
    days() {
      return Number(this.widget.param.days) || 30
    },
    title() {
      return this.widget.param.title || (this.indicator ? this.indicator.name : '')
    },
    indicatorEn() {
      return this.indicator ? this.indicator.en : ''
    },
    unit() {
      return this.indicator ? this.indicator.unit : ''
    },
    // ascending by date, one (last) value per day
    series() {
      const byDay = {}
      const ordered = [...this.measures].sort((a, b) => a.date - b.date)
      ordered.forEach(m => {
        if (m.value !== null) byDay[m.day] = m.value
      })
      return Object.keys(byDay).sort().map(day => ({ day, value: byDay[day] }))
    },
    values() {
      return this.series.map(s => s.value)
    },
    lastText() {
      if (!this.values.length) return '—'
      return String(this.values[this.values.length - 1])
    },
    delta() {
      const v = this.values
      if (v.length < 2) return 0
      const last7 = v.slice(-7)
      const prev7 = v.slice(-14, -7)
      if (!prev7.length) return v[v.length - 1] - v[0]
      const avg = arr => arr.reduce((a, b) => a + b, 0) / arr.length
      return avg(last7) - avg(prev7)
    },
    deltaText() {
      if (this.values.length < 2) return 'мало данных'
      const d = this.delta
      return `${d > 0 ? '+' : ''}${d.toFixed(1)} к прошлой неделе`
    },
    trend() {
      if (this.values.length < 2) return 'ok'
      const goodDown = this.widget.param.gooddown !== false
      if (Math.abs(this.delta) < 0.4) return 'stable'
      const better = goodDown ? this.delta < 0 : this.delta > 0
      return better ? 'better' : 'worse'
    },
    trendLabel() {
      if (this.trend === 'better') return 'Стало лучше'
      if (this.trend === 'worse') return 'Стало хуже'
      return 'Стабильно'
    },
    trendStyle() {
      const z = this.trend === 'worse' ? HBT_ZONES.warn : HBT_ZONES.ok
      return { background: z.bg, color: z.color, borderColor: z.border }
    },
    points() {
      const v = this.values
      if (v.length < 2) return ''
      const w = 320
      const h = 88
      const min = Math.min(...v)
      const max = Math.max(...v)
      const span = max - min || 1
      return v.map((val, i) => {
        const x = (i / (v.length - 1)) * (w - 6) + 3
        const y = h - 6 - ((val - min) / span) * (h - 12)
        return `${x.toFixed(1)},${y.toFixed(1)}`
      }).join(' ')
    },
    minLabel() {
      return this.values.length ? `мин ${Math.min(...this.values)}` : ''
    },
    maxLabel() {
      return this.values.length ? `макс ${Math.max(...this.values)}` : ''
    },
  },
  created() {
    this.load()
    this.$root.$on(HBT_REFRESH, this.reload)
  },
  beforeDestroy() {
    this.$root.$off(HBT_REFRESH, this.reload)
  },
  methods: {
    async load() {
      const indicators = await hbtLoadIndicators(this, this.widget.param)
      const wanted = this.widget.param.indicator && this.widget.param.indicator.sid
      this.indicator = indicators.find(i => i.sid === wanted) || indicators[0] || null
      await this.reload()
    },
    async reload() {
      if (!this.indicator) return
      this.measures = await hbtLoadMeasures(this, this.widget.param, this.days, this.indicator.id)
    },
  },
}
</script>

<style src="./habits/habits.css"></style>
<style scoped>
.hbt-chart-head {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
  border-top: 2px solid rgba(32, 30, 29, 0.4);
  padding-top: 14px;
}
</style>
